import React, { useState, useEffect } from 'react';
import { Card, Calendar, Badge, Typography, Space, Spin, Alert, Modal, Table, Statistic, Row, Col, Button, Collapse } from 'antd';
import { CalendarOutlined, ArrowUpOutlined, ArrowDownOutlined, InfoCircleOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import moment from 'moment';
import dailyPriceService from '../services/dailyPriceService';

const { Text, Title } = Typography;

const DailyPriceCalendar = ({ stockSymbols = [], stockList = [], broker = null, title = '종가 캘린더' }) => {
  const [calendarData, setCalendarData] = useState({});
  const [portfolioData, setPortfolioData] = useState({}); // 포트폴리오 데이터 추가
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedDateData, setSelectedDateData] = useState([]);
  const [dividendData, setDividendData] = useState(null); // 배당 데이터 추가
  const [quantityChanges, setQuantityChanges] = useState(null); // 수량 변화 데이터 추가
  const [detailsLoading, setDetailsLoading] = useState(false); // 상세 정보 로딩 상태
  const [currentMonth, setCurrentMonth] = useState(moment());
  const [calendarMode, setCalendarMode] = useState('month');

  // Helper function to get stock name from symbol
  const getStockName = (symbol) => {
    const stock = stockList.find(s => s.symbol === symbol);
    return stock ? stock.name : symbol;
  };

  // 현재 패널(month/year)의 데이터 로드
  useEffect(() => {
    if (stockSymbols.length > 0) {
      loadCalendarData(currentMonth, calendarMode);
    }
  }, [stockSymbols, currentMonth, calendarMode, broker]);

  const loadCalendarData = async (baseDate, mode = 'month') => {
    if (stockSymbols.length === 0) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 현재 캘린더 패널의 시작일과 종료일 계산
      const rangeUnit = mode === 'year' ? 'year' : 'month';
      const startDate = baseDate.clone().startOf(rangeUnit).format('YYYY-MM-DD');
      const endDate = baseDate.clone().endOf(rangeUnit).format('YYYY-MM-DD');

      // console.log(`📅 ${baseDate.format(mode === 'year' ? 'YYYY' : 'YYYY-MM')} 종가 데이터 로드 (최적화된 API 사용):`, { startDate, endDate, stockSymbols });

      // 날짜 범위를 일수로 계산
      const daysDiff = moment(endDate).diff(moment(startDate), 'days') + 1;
      
      // 최적화된 다중 종목 API 사용
      const holdingsPrices = await dailyPriceService.getHoldingsRecentPrices(
        stockSymbols,
        daysDiff,
        broker,
        startDate,
        endDate
      );

      // 날짜별로 데이터 정리
      const rangeData = {};
      
      Object.keys(holdingsPrices).forEach(symbol => {
        const stockData = holdingsPrices[symbol];
        
        if (stockData && stockData.success && stockData.data && stockData.data.length > 0) {
          // 조회 범위 내의 데이터만 필터링
          const filteredData = stockData.data.filter(item => {
            const itemDate = moment(item.date);
            return itemDate.isSameOrAfter(startDate) && itemDate.isSameOrBefore(endDate);
          });

          // 각 종목의 데이터에 변화율 계산
          const dataWithChanges = dailyPriceService.calculatePriceChanges(filteredData);
          
          dataWithChanges.forEach(priceItem => {
            const date = priceItem.date;
            if (!rangeData[date]) {
              rangeData[date] = [];
            }
            
            // 백엔드에서 계산된 값이 있으면 우선 사용, 없으면 프론트엔드 계산값 사용
            const backendChangeRate = parseFloat(priceItem.change_rate || 0);
            const backendChangeAmount = parseFloat(priceItem.change_amount || 0);
            const frontendChangeRate = parseFloat(priceItem.change_percent_from_prev || 0);
            const frontendChangeAmount = parseFloat(priceItem.change_from_prev || 0);
            
            rangeData[date].push({
              ...priceItem,
              symbol: symbol,
              // 백엔드 값이 0이 아니면 백엔드 값 사용, 아니면 프론트엔드 계산값 사용
              change_rate: (backendChangeRate !== 0) ? backendChangeRate : frontendChangeRate,
              change_amount: (backendChangeAmount !== 0) ? backendChangeAmount : frontendChangeAmount
            });
          });
        }
      });

      setCalendarData(rangeData);
      // console.log('✅ 종가 데이터 로드 완료 (최적화됨):', Object.keys(rangeData).length, '일');

      // 포트폴리오 데이터 로드 (기간 첫날 비교를 위해 7일 전부터 로드)
      try {
        const portfolioStartDate = moment(startDate).subtract(7, 'days').format('YYYY-MM-DD');
        const portfolioResponse = await dailyPriceService.getPortfolioTrend({
          date_from: portfolioStartDate,
          date_to: endDate
        });

        if (portfolioResponse && portfolioResponse.success && portfolioResponse.data) {
          // 날짜별 포트폴리오 데이터를 맵으로 변환
          // hasValidData가 true인 데이터만 표시 (주말/휴장일 제외)
          const portfolioMap = {};
          portfolioResponse.data.forEach(item => {
            // 실제 데이터가 있는 날짜만 추가
            if (item.hasValidData) {
              portfolioMap[item.date] = item;
            }
          });
          setPortfolioData(portfolioMap);
          // console.log('✅ 포트폴리오 데이터 로드 완료:', Object.keys(portfolioMap).length, '일');
        }
      } catch (portfolioError) {
        console.error('⚠️ 포트폴리오 데이터 로드 실패 (계속 진행):', portfolioError);
        // 포트폴리오 데이터 로드 실패는 전체 에러로 처리하지 않음
        setPortfolioData({});
      }
    } catch (error) {
      console.error('❌ 월별 종가 데이터 로드 실패:', error);
      setError(error.message || '종가 데이터를 불러올 수 없습니다.');
      setCalendarData({});
      setPortfolioData({});
    } finally {
      setLoading(false);
    }
  };

  // 특정 날짜의 포트폴리오 전일 대비 증감 계산
  const getPortfolioChangeForDate = (date) => {
    if (!date) return null;

    const dateStr = date.format('YYYY-MM-DD');
    const portfolioInfo = portfolioData[dateStr];

    if (!portfolioInfo || !portfolioInfo.hasValidData) {
      return null;
    }

    // 가장 가까운 이전 날짜의 유효한 데이터 찾기 (주말/휴장일 대응)
    let prevPortfolioInfo = null;
    let prevDate = date.clone().subtract(1, 'day');
    const maxSearchDays = 7; // 최대 7일 전까지 검색
    let searchCount = 0;

    while (searchCount < maxSearchDays) {
      const prevDateStr = prevDate.format('YYYY-MM-DD');
      const candidate = portfolioData[prevDateStr];

      if (candidate && candidate.hasValidData) {
        prevPortfolioInfo = candidate;
        break;
      }

      prevDate = prevDate.subtract(1, 'day');
      searchCount++;
    }

    if (!prevPortfolioInfo || prevPortfolioInfo.total_assets <= 0) {
      return null;
    }

    const portfolioChange = portfolioInfo.total_assets - prevPortfolioInfo.total_assets;
    const portfolioChangePercent = (portfolioChange / prevPortfolioInfo.total_assets) * 100;

    return {
      portfolioChange,
      portfolioChangePercent
    };
  };

  // 날짜 셀 렌더링
  const dateCellRender = (value) => {
    if (calendarMode !== 'month') {
      return null;
    }

    const dateStr = value.format('YYYY-MM-DD');
    const dayData = calendarData[dateStr] || [];
    const portfolioChangeInfo = getPortfolioChangeForDate(value);

    if (dayData.length === 0 && !portfolioChangeInfo) {
      return null;
    }

    const totalStocks = dayData.length;
    const portfolioChange = portfolioChangeInfo?.portfolioChange;
    const portfolioChangePercent = portfolioChangeInfo?.portfolioChangePercent;
    const changeColor = portfolioChange >= 0 ? '#52c41a' : '#ff4d4f';

    return (
      <div style={{ fontSize: '12px' }}>
        {totalStocks > 0 && (
          <div style={{ marginBottom: '2px' }}>
          <Badge 
            count={totalStocks} 
            size="small" 
            style={{ backgroundColor: '#1890ff' }}
          />
          </div>
        )}
        {portfolioChangeInfo && (
          <div style={{ 
            color: changeColor,
            fontWeight: 'bold',
            lineHeight: 1.3
          }}>
            <div>
              {portfolioChange >= 0 ? '↑' : '↓'}{Math.abs(Math.round(portfolioChange)).toLocaleString()}원
            </div>
            <div style={{ fontSize: '10px' }}>
              ({portfolioChangePercent >= 0 ? '+' : ''}{portfolioChangePercent.toFixed(2)}%)
            </div>
          </div>
        )}
      </div>
    );
  };

  // 연간 패널의 월별 요약 렌더링
  const monthCellRender = (value) => {
    if (calendarMode !== 'year') {
      return null;
    }

    const monthKey = value.format('YYYY-MM');
    const monthDates = Object.keys(calendarData)
      .filter(date => date.startsWith(monthKey))
      .sort();
    const monthPortfolioDates = Object.keys(portfolioData)
      .filter(date => date.startsWith(monthKey))
      .sort();

    if (monthDates.length === 0 && monthPortfolioDates.length === 0) {
      return (
        <div style={{ padding: '8px 4px', color: '#999', fontSize: '12px' }}>
          데이터 없음
        </div>
      );
    }

    const changes = monthDates.flatMap(date =>
      (calendarData[date] || []).map(item => parseFloat(item.change_rate || 0))
    );
    const avgChange = changes.length > 0
      ? changes.reduce((sum, change) => sum + change, 0) / changes.length
      : 0;
    const positiveChanges = changes.filter(change => change > 0).length;
    const negativeChanges = changes.filter(change => change < 0).length;

    const firstPortfolio = monthPortfolioDates.length > 0 ? portfolioData[monthPortfolioDates[0]] : null;
    const lastPortfolio = monthPortfolioDates.length > 0 ? portfolioData[monthPortfolioDates[monthPortfolioDates.length - 1]] : null;
    const monthlyAssetChange = firstPortfolio && lastPortfolio
      ? lastPortfolio.total_assets - firstPortfolio.total_assets
      : null;
    const monthlyAssetChangePercent = firstPortfolio && firstPortfolio.total_assets > 0 && monthlyAssetChange !== null
      ? (monthlyAssetChange / firstPortfolio.total_assets) * 100
      : null;

    return (
      <div style={{ padding: '6px 4px', fontSize: '12px', lineHeight: 1.5 }}>
        {monthlyAssetChange !== null && (
          <div style={{
            color: monthlyAssetChange >= 0 ? '#52c41a' : '#ff4d4f',
            fontWeight: 'bold',
            marginBottom: 4
          }}>
            자산 {monthlyAssetChange >= 0 ? '+' : ''}{monthlyAssetChange.toLocaleString()}원
            {monthlyAssetChangePercent !== null && (
              <span> ({monthlyAssetChangePercent >= 0 ? '+' : ''}{monthlyAssetChangePercent.toFixed(2)}%)</span>
            )}
          </div>
        )}
        {changes.length > 0 && (
          <>
            <div style={{ color: avgChange >= 0 ? '#52c41a' : '#ff4d4f' }}>
              평균 종가 {avgChange >= 0 ? '+' : ''}{avgChange.toFixed(2)}%
            </div>
            <div style={{ color: '#666' }}>
              거래일 {monthDates.length}일 · <span style={{ color: '#52c41a' }}>↑{positiveChanges}</span>{' '}
              <span style={{ color: '#ff4d4f' }}>↓{negativeChanges}</span>
            </div>
          </>
        )}
      </div>
    );
  };

  // 날짜 선택 핸들러
  const onSelect = async (value) => {
    if (calendarMode === 'year') {
      setCurrentMonth(value);
      setCalendarMode('month');
      return;
    }

    const dateStr = value.format('YYYY-MM-DD');
    const dayData = calendarData[dateStr] || [];
    const portfolioInfo = portfolioData[dateStr];

    if (dayData.length > 0 || portfolioInfo) {
      // console.log('📊 선택된 날짜 데이터:', dayData);
      setSelectedDate(value);
      setSelectedDateData(dayData);
      setDividendData(null);
      setQuantityChanges(null);
      setModalVisible(true);
      
      // 배당 및 수량 변화 정보 로드
      setDetailsLoading(true);
      try {
        const details = await dailyPriceService.getDailyHoldingDateDetails(dateStr);
        if (details && details.success) {
          setDividendData(details.dividends);
          setQuantityChanges(details.quantity_changes);
        }
      } catch (error) {
        console.error('❌ 날짜 상세 정보 로드 실패:', error);
        // 에러가 나도 모달은 계속 표시
      } finally {
        setDetailsLoading(false);
      }
    }
  };

  // 월 변경 핸들러
  const onPanelChange = (value, mode) => {
    setCurrentMonth(value);
    setCalendarMode(mode);
  };

  // 이전 기간으로 이동
  const goToPreviousPeriod = () => {
    const prevPeriod = moment(currentMonth).subtract(1, calendarMode === 'year' ? 'year' : 'month');
    setCurrentMonth(prevPeriod);
  };

  // 다음 기간으로 이동
  const goToNextPeriod = () => {
    const nextPeriod = moment(currentMonth).add(1, calendarMode === 'year' ? 'year' : 'month');
    setCurrentMonth(nextPeriod);
  };

  // 현재 기간으로 이동
  const goToCurrentPeriod = () => {
    setCurrentMonth(moment());
  };

  // 모달 테이블 컬럼
  const modalColumns = [
    {
      title: '종목',
      dataIndex: 'symbol',
      key: 'symbol',
      width: 120,
      render: (symbol, record) => {
        const stockName = getStockName(symbol);
        const displayName = stockName !== symbol ? `${stockName}(${symbol})` : symbol;
        return (
          <div>
            <Text strong>{stockName}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {symbol}
            </Text>
          </div>
        );
      }
    },
    {
      title: '종가',
      dataIndex: 'closing_price',
      key: 'closing_price',
      width: 100,
      align: 'right',
      render: (price) => `₩${parseFloat(price).toLocaleString()}`
    },
    {
      title: '변화',
      dataIndex: 'change_amount',
      key: 'change_amount',
      width: 100,
      align: 'right',
      render: (change, record) => {
        const changeAmount = parseFloat(change || 0);
        const changeRate = parseFloat(record.change_rate || 0);
        return (
          <div style={{ color: changeAmount >= 0 ? '#52c41a' : '#ff4d4f' }}>
            <div>
              {changeAmount >= 0 ? '+' : ''}₩{Math.abs(changeAmount).toLocaleString()}
            </div>
            <div style={{ fontSize: '12px' }}>
              ({changeRate >= 0 ? '+' : ''}{changeRate.toFixed(2)}%)
            </div>
          </div>
        );
      }
    },
    {
      title: '거래량',
      dataIndex: 'volume',
      key: 'volume',
      width: 100,
      align: 'right',
      render: (volume) => volume ? parseInt(volume).toLocaleString() : '-'
    }
  ];

  // 선택된 날짜의 통계 계산
  const getSelectedDateStats = () => {
    if (selectedDateData.length === 0) {
      return null;
    }

    const changes = selectedDateData.map(item => parseFloat(item.change_rate || 0));
    const positiveCount = changes.filter(c => c > 0).length;
    const negativeCount = changes.filter(c => c < 0).length;
    const avgChange = changes.reduce((sum, c) => sum + c, 0) / changes.length;
    const totalVolume = selectedDateData.reduce((sum, item) => sum + (parseInt(item.volume) || 0), 0);

    return {
      totalStocks: selectedDateData.length,
      positiveCount,
      negativeCount,
      avgChange,
      totalVolume
    };
  };

  // 선택된 날짜의 포트폴리오 정보 계산
  const getSelectedPortfolioInfo = () => {
    if (!selectedDate) return null;
    
    const dateStr = selectedDate.format('YYYY-MM-DD');
    const portfolioInfo = portfolioData[dateStr];
    
    if (!portfolioInfo || !portfolioInfo.hasValidData) {
      return null;
    }

    const portfolioChangeInfo = getPortfolioChangeForDate(selectedDate);

    return {
      totalAssets: portfolioInfo.total_assets,
      totalMarketValue: portfolioInfo.total_market_value,
      totalCashBalance: portfolioInfo.total_cash_balance,
      totalUnrealizedPnL: portfolioInfo.total_unrealized_pnl,
      profitLossRate: portfolioInfo.profit_loss_rate,
      portfolioChange: portfolioChangeInfo?.portfolioChange ?? null,
      portfolioChangePercent: portfolioChangeInfo?.portfolioChangePercent ?? null
    };
  };

  const dateStats = getSelectedDateStats();
  const portfolioInfo = getSelectedPortfolioInfo();

  return (
    <Card
      title={
        <Space>
          <CalendarOutlined />
          <span>{title}</span>
        </Space>
      }
    >
      {error && (
        <Alert
          message="데이터 로드 실패"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {stockSymbols.length === 0 && (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <CalendarOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
          <Title level={4} type="secondary">보유 종목이 없습니다</Title>
          <Text type="secondary">종목을 보유하면 일별 종가 변화를 캘린더에서 확인할 수 있습니다.</Text>
        </div>
      )}

      {stockSymbols.length > 0 && (
        <>
          {/* 캘린더 네비게이션 */}
          <div style={{ marginBottom: 16, textAlign: 'center' }}>
            <Space size="middle">
              <Button
                type="text"
                icon={<LeftOutlined />}
                onClick={goToPreviousPeriod}
                disabled={loading}
              >
                {calendarMode === 'year' ? '이전 년' : '이전 월'}
              </Button>
              
              <Button
                type="primary"
                ghost
                onClick={goToCurrentPeriod}
                disabled={loading}
              >
                {calendarMode === 'year' ? currentMonth.format('YYYY년') : currentMonth.format('YYYY년 MM월')}
              </Button>
              
              <Button
                type="text"
                icon={<RightOutlined />}
                onClick={goToNextPeriod}
                disabled={loading}
              >
                {calendarMode === 'year' ? '다음 년' : '다음 월'}
              </Button>
            </Space>
          </div>

          <Spin spinning={loading} tip="데이터 로딩 중...">
            <Calendar
              dateCellRender={dateCellRender}
              monthCellRender={monthCellRender}
              onSelect={onSelect}
              onPanelChange={onPanelChange}
              mode={calendarMode}
              value={currentMonth}
            />
          </Spin>
        </>
      )}

      {/* 날짜별 상세 정보 모달 */}
      <Modal
        title={
          <Space>
            <CalendarOutlined />
            <span>{selectedDate?.format('YYYY년 MM월 DD일')} 종가 정보</span>
          </Space>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
      >
        {/* 포트폴리오 정보 */}
        {portfolioInfo && (
          <>
            <div style={{ marginBottom: 24 }}>
              <Title level={5} style={{ marginBottom: 16 }}>
                💰 포트폴리오 요약
              </Title>
              
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col xs={12} sm={8}>
                  <Statistic
                    title="총 자산"
                    value={portfolioInfo.totalAssets}
                    precision={0}
                    suffix="원"
                    valueStyle={{ fontSize: '16px', color: '#1890ff' }}
                  />
                </Col>
                <Col xs={12} sm={8}>
                  <Statistic
                    title="주식 평가액"
                    value={portfolioInfo.totalMarketValue}
                    precision={0}
                    suffix="원"
                    valueStyle={{ fontSize: '16px' }}
                  />
                </Col>
                <Col xs={12} sm={8}>
                  <Statistic
                    title="현금 잔고"
                    value={portfolioInfo.totalCashBalance}
                    precision={0}
                    suffix="원"
                    valueStyle={{ fontSize: '16px' }}
                  />
                </Col>
              </Row>

              {portfolioInfo.portfolioChange !== null && (
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Statistic
                      title="전일 대비 증감"
                      value={Math.abs(portfolioInfo.portfolioChange)}
                      precision={0}
                      suffix="원"
                      prefix={portfolioInfo.portfolioChange >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                      valueStyle={{ 
                        fontSize: '16px',
                        color: portfolioInfo.portfolioChange >= 0 ? '#52c41a' : '#ff4d4f'
                      }}
                    />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      ({portfolioInfo.portfolioChangePercent >= 0 ? '+' : ''}
                      {portfolioInfo.portfolioChangePercent.toFixed(2)}%)
                    </Text>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Statistic
                      title="누적 수익률"
                      value={Math.abs(portfolioInfo.profitLossRate)}
                      precision={2}
                      suffix="%"
                      prefix={portfolioInfo.profitLossRate >= 0 ? '+' : '-'}
                      valueStyle={{ 
                        fontSize: '16px',
                        color: portfolioInfo.profitLossRate >= 0 ? '#52c41a' : '#ff4d4f'
                      }}
                    />
                  </Col>
                </Row>
              )}
            </div>
            
            <div style={{ 
              borderTop: '2px solid #f0f0f0', 
              paddingTop: 16,
              marginBottom: 24
            }} />
          </>
        )}

        {/* 배당 및 수량 변화 정보 */}
        {detailsLoading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin tip="배당 및 수량 변화 정보 로딩 중..." />
          </div>
        ) : (
          <>
            {(dividendData && dividendData.count > 0) || (quantityChanges && quantityChanges.count > 0) ? (
              <Collapse
                defaultActiveKey={quantityChanges && quantityChanges.count > 0 ? ['quantity'] : []}
                style={{ marginBottom: 24 }}
              >
                {/* 배당 정보 */}
                {dividendData && dividendData.count > 0 && (
                  <Collapse.Panel
                    header={
                      <span>
                        💰 <strong>배당금 수령</strong> ({dividendData.count}건, 총 ₩{dividendData.total_amount.toLocaleString()})
                      </span>
                    }
                    key="dividend"
                  >
                    <Table
                      dataSource={dividendData.items}
                      columns={[
                        {
                          title: '계좌',
                          dataIndex: 'account_name',
                          key: 'account_name',
                          width: 150
                        },
                        {
                          title: '종목',
                          dataIndex: 'stock_name',
                          key: 'stock_name',
                          width: 180,
                          render: (name, record) => (
                            <div>
                              <Text strong>{name}</Text>
                              <br />
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                {record.stock_symbol}
                              </Text>
                            </div>
                          )
                        },
                        {
                          title: '수량',
                          dataIndex: 'quantity',
                          key: 'quantity',
                          width: 80,
                          align: 'right',
                          render: (qty) => `${qty.toLocaleString()}주`
                        },
                        {
                          title: '주당 배당',
                          dataIndex: 'dividend_per_share',
                          key: 'dividend_per_share',
                          width: 100,
                          align: 'right',
                          render: (amount) => `₩${amount.toLocaleString()}`
                        },
                        {
                          title: '총 배당금',
                          dataIndex: 'total_amount',
                          key: 'total_amount',
                          width: 120,
                          align: 'right',
                          render: (amount) => (
                            <Text strong style={{ color: '#52c41a' }}>
                              ₩{amount.toLocaleString()}
                            </Text>
                          )
                        }
                      ]}
                      rowKey={(record, index) => `dividend-${index}`}
                      pagination={false}
                      size="small"
                    />
                  </Collapse.Panel>
                )}

                {/* 수량 변화 정보 */}
                {quantityChanges && quantityChanges.count > 0 && (
                  <Collapse.Panel
                    header={
                      <span>
                        📊 <strong>보유 수량 변화</strong> ({quantityChanges.count}건)
                      </span>
                    }
                    key="quantity"
                  >
                    <Table
                      dataSource={quantityChanges.items}
                      columns={[
                        {
                          title: '계좌',
                          dataIndex: 'account_name',
                          key: 'account_name',
                          width: 150
                        },
                        {
                          title: '종목',
                          dataIndex: 'stock_name',
                          key: 'stock_name',
                          width: 180,
                          render: (name, record) => (
                            <div>
                              <Text strong>{name}</Text>
                              <br />
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                {record.stock_symbol}
                              </Text>
                            </div>
                          )
                        },
                        {
                          title: '전일',
                          dataIndex: 'quantity_yesterday',
                          key: 'quantity_yesterday',
                          width: 80,
                          align: 'right',
                          render: (qty) => `${qty.toLocaleString()}주`
                        },
                        {
                          title: '당일',
                          dataIndex: 'quantity_today',
                          key: 'quantity_today',
                          width: 80,
                          align: 'right',
                          render: (qty) => `${qty.toLocaleString()}주`
                        },
                        {
                          title: '변화',
                          dataIndex: 'quantity_change',
                          key: 'quantity_change',
                          width: 100,
                          align: 'right',
                          render: (change, record) => {
                            const isNew = record.change_type === 'new';
                            const isSoldAll = record.change_type === 'sold_all';
                            const isIncrease = change > 0;
                            
                            return (
                              <div style={{ 
                                color: isSoldAll ? '#ff4d4f' : (isNew ? '#1890ff' : (isIncrease ? '#52c41a' : '#ff4d4f'))
                              }}>
                                <div>
                                  {isNew ? '신규' : isSoldAll ? '전량 매도' : `${change > 0 ? '+' : ''}${change.toLocaleString()}주`}
                                </div>
                                {isIncrease && !isNew && !isSoldAll && <span>📈</span>}
                                {!isIncrease && !isNew && change !== 0 && !isSoldAll && <span>📉</span>}
                                {isSoldAll && <span>🚨</span>}
                              </div>
                            );
                          }
                        }
                      ]}
                      rowKey={(record, index) => `quantity-change-${index}`}
                      pagination={false}
                      size="small"
                    />
                  </Collapse.Panel>
                )}
              </Collapse>
            ) : null}
          </>
        )}

        {dateStats && (
          <>
            {/* 날짜별 통계 */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col xs={12} sm={6}>
                <Statistic
                  title="총 종목"
                  value={dateStats.totalStocks}
                  suffix="개"
                  valueStyle={{ fontSize: '16px' }}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="상승"
                  value={dateStats.positiveCount}
                  suffix="개"
                  valueStyle={{ fontSize: '16px', color: '#52c41a' }}
                  prefix={<ArrowUpOutlined />}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="하락"
                  value={dateStats.negativeCount}
                  suffix="개"
                  valueStyle={{ fontSize: '16px', color: '#ff4d4f' }}
                  prefix={<ArrowDownOutlined />}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="평균 변화율"
                  value={Math.abs(dateStats.avgChange)}
                  precision={2}
                  suffix="%"
                  valueStyle={{ 
                    fontSize: '16px',
                    color: dateStats.avgChange >= 0 ? '#52c41a' : '#ff4d4f'
                  }}
                  prefix={dateStats.avgChange >= 0 ? '+' : '-'}
                />
              </Col>
            </Row>

            {/* 종목별 상세 테이블 */}
            <Table
              columns={modalColumns}
              dataSource={selectedDateData}
              rowKey={(record, index) => `${record.symbol}-${record.date}-${index}`}
              pagination={false}
              size="small"
              scroll={{ y: 300 }}
            />
          </>
        )}
      </Modal>
    </Card>
  );
};

export default DailyPriceCalendar;
